'use client';

import { type VariantProps, cva, cx } from 'class-variance-authority';
import Icon, { type IconName } from './Icon';
import { type ReactNode, useEffect, useRef } from 'react';

export enum Grouped {
    None = 'none',
    Left = 'left',
    Center = 'center',
    Right = 'right',
}

const variants = cva('', {
    variants: {
        grouped: {
            none: 'rounded-4xl',
            left: 'rounded-l-4xl',
            center: '',
            right: 'rounded-r-4xl',
        },
    },
    defaultVariants: {
        grouped: Grouped.None,
    },
});

type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

export default function Input<T extends React.HTMLInputTypeAttribute>({
    iconBefore,
    iconAfter,
    actionBefore,
    actionAfter,
    className,
    iconClassName,
    hasError = false,
    grouped = Grouped.None,
    hasClear = false,
    disabled,
    isLoading: _isLoading,
    focus = false,
    noPadding = false,
    prefix,
    suffix,
    ...props
}: Omit<InputProps, 'type' | 'value' | 'prefix' | 'suffix'> & {
    value?: T extends 'date'
        ? Date | null | string
        : T extends 'text'
          ? string | undefined
          : string | number | undefined;
    type?: T;
    asChild?: boolean;
    iconBefore?: IconName | React.JSX.Element | ReactNode;
    iconAfter?: IconName | React.JSX.Element | ReactNode;
    actionBefore?: React.ReactNode;
    actionAfter?: React.ReactNode;
    className?: string;
    hasError?: boolean;
    hasClear?: boolean;
    grouped?: NonNullable<VariantProps<typeof variants>['grouped']>;
    disabled?: boolean;
    iconClassName?: string;
    isLoading?: boolean;
    noPadding?: boolean;
    focus?: boolean;
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
}) {
    const hasIconBefore = !!iconBefore;
    const hasIconAfter = !!iconAfter;
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        requestAnimationFrame(() => {
            if (inputRef.current && focus) inputRef?.current.focus();
        });
    }, [inputRef, focus]);

    return (
        <label
            className={cx(
                'relative flex-1 focus-within:z-1',
                'flex w-full items-center border-0 bg-white leading-none text-primary ring-1 transition',
                'focus-within:ring-turqoise',
                disabled ? 'bg-blue-100 text-blue-400' : 'hover:ring-blue',
                hasError
                    ? 'text-orange-700 placeholder-orange-300 ring-orange-700'
                    : 'ring-blue-200',
                className,
                variants({ grouped }),
            )}
        >
            {actionBefore && <div className="ml-1">{actionBefore}</div>}

            {iconBefore &&
                (Icon.isIconName(iconBefore) ? (
                    <InputIcon
                        hasError={hasError}
                        name={iconBefore}
                        className={cx('left-4', iconClassName ?? 'text-secondary')}
                    />
                ) : (
                    iconBefore
                ))}

            <div
                className={cx(
                    'flex w-full items-center gap-2',
                    hasIconBefore ? 'pl-11' : !noPadding ? 'pl-5' : '',
                    hasIconAfter ? 'pr-11' : !noPadding ? 'pr-5' : '',
                    actionBefore && actionAfter ? 'mx-auto' : '',
                )}
            >
                {prefix && affix(prefix)}
                <input
                    {...props}
                    className={cx(
                        'w-full appearance-none border-0 bg-transparent p-0 py-4 outline-hidden placeholder:text-blue-400 focus:ring-0',
                        disabled && 'user-select-none cursor-not-allowed',
                    )}
                    ref={inputRef}
                    disabled={disabled}
                    onFocus={event => event.target.select()}
                    aria-invalid={hasError}
                    value={
                        (props.value instanceof Date
                            ? props.value.toISOString().split('T').at(0)
                            : props.value) ?? ''
                    }
                    onChange={props.onChange}
                />
                {suffix && affix(suffix)}
            </div>
            {iconAfter &&
                (Icon.isIconName(iconAfter) ? (
                    <InputIcon
                        hasError={hasError}
                        name={iconAfter}
                        className={cx('right-4', iconClassName ?? 'text-turqoise-500')}
                    />
                ) : (
                    iconAfter
                ))}

            {hasClear && props.value && (
                <button
                    type="button"
                    onClick={() =>
                        props.name &&
                        props.onChange?.({
                            target: { value: null, name: props.name },
                        } as any)
                    }
                >
                    <Icon name="close" size="xs" className="mr-4 opacity-50 hover:opacity-100" />
                </button>
            )}
            {actionAfter && <div className="mr-1">{actionAfter}</div>}
        </label>
    );
}

export function InputIcon(props: { hasError: boolean; name: IconName; className: string }) {
    return (
        <Icon
            className={cx(
                'absolute top-1/2 -translate-y-1/2',
                props.hasError && 'text-orange-700',
                props.className,
            )}
            name={props.name}
        />
    );
}

const affix = (affix: React.ReactNode) =>
    typeof affix === 'string' && affix.length ? (
        <span className="text-sm text-blue-500">{affix}</span>
    ) : (
        affix
    );
